import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Condition, Filter } from 'components/condition-list/SearchFilter';
import { generateUUID } from 'utils/Common';

/**
 * URL のクエリパラメータから、検索条件（Condition[]）に対応する初期フィルター（Filter[]）を組み立てる。
 *
 * - クエリのキーは Condition.key に一致するものだけを対象にする。
 * - 値の表示名（チップ表示）は Condition の型に応じて解決する:
 *   - select / radio: values から name を引く
 *   - user/manager/client/member/partner/project-select: 対応リストから名称を引く
 *   - text / katakana-text / date系: 値をそのまま表示に使う
 *   - check: 値が 'true'（または任意の非空）のときのみ Condition.value をそのまま採用
 * - 解決できない値でもキーが一致すれば、値をそのまま key/name に用いてフィルタ化する（APIには生値が渡る）。
 *
 * これにより「稼働中のプロジェクト（?status=ACTIVE&managerId=xxx）」のような
 * ウィジェットからの遷移で初期検索条件を復元できる。API には絡まない、画面内の初期化のみ。
 */
export function buildInitialFiltersFromQuery(
  conditions: Condition[],
  params: URLSearchParams,
): Filter[] {
  const filters: Filter[] = [];

  conditions.forEach((c) => {
    const raw = params.get(c.key);
    if (raw === null || raw === '') return;

    const make = (key: string, label: string) =>
      filters.push({
        id: generateUUID(),
        key: c.key,
        color: c.color,
        value: { key, name: `${c.name}: ${label}` },
      });

    switch (c.type) {
      case 'select':
      case 'radio': {
        const found = c.values?.find((v) => v.key === raw);
        make(raw, found ? found.name : raw);
        break;
      }
      case 'select-multi': {
        // カンマ区切りのキー群を1フィルタ化。表示名は values から解決して結合する
        const keys = raw.split(',').filter(Boolean);
        const names = keys.map((k) => c.values?.find((v) => v.key === k)?.name ?? k).join(', ');
        make(keys.join(','), names);
        break;
      }
      case 'user-select-single':
      case 'manager-select-single': {
        const list = c.type === 'manager-select-single' ? c.managers : c.users;
        const found = list?.find((u) => u.userId === raw);
        make(raw, found ? found.userName : raw);
        break;
      }
      case 'client-select-single': {
        const found = c.clients?.find((x) => x.clientId === raw);
        make(raw, found ? found.clientName : raw);
        break;
      }
      case 'member-select-single': {
        const found = c.members?.find((x) => x.memberId === raw);
        make(raw, found ? found.memberName : raw);
        break;
      }
      case 'partner-select-single': {
        const found = c.partners?.find((x) => x.partnerId === raw);
        make(raw, found ? found.partnerName : raw);
        break;
      }
      case 'project-select-single': {
        const found = c.projects?.find((x) => x.projectId === raw);
        make(raw, found ? found.projectName : raw);
        break;
      }
      case 'ticket-select-single': {
        // チップ表示は管理ID（候補が未取得でも生値=ticketIdでフィルタ化）
        const found = c.tickets?.find((x) => x.ticketId === raw);
        make(raw, found ? found.managementId : raw);
        break;
      }
      case 'financial-year-select': {
        make(raw, `${raw}期`);
        break;
      }
      case 'check': {
        // 非空（'true' 等）のときだけ有効化。表示名は Condition.name。
        if (c.value) make(c.value.key, c.name);
        break;
      }
      case 'date-time-picker-start':
      case 'date-time-picker-end': {
        make(raw, raw.substring(0, 10));
        break;
      }
      default: {
        // text / katakana-text
        make(raw, raw);
      }
    }
  });

  return filters;
}

/**
 * コンソール一覧画面で URL クエリパラメータを初期検索条件として扱うための共通フック。
 *
 * - `queryParams`: API 検索に渡す生パラメータ（`managerId=self` などの特別値は解決オプションで変換）。
 * - `initialFilters`: 検索バーのチップ表示用フィルター（`searchConditions` でラベル解決）。
 *
 * @param conditions 画面の検索条件定義
 * @param options.selfUserId 指定時、値が 'self' のキーをこのユーザーIDへ解決する（例: managerId=self）
 * @param options.selfKeys 'self' 解決を許可するクエリキー（既定: ['managerId','personInChargeId','userId','managerUserId']）
 */
export function useQueryInitialFilters(
  conditions: Condition[],
  options?: { selfUserId?: string; selfKeys?: string[] },
): { queryParams: Record<string, string>; initialFilters: Filter[] } {
  const [searchParams] = useSearchParams();
  const selfUserId = options?.selfUserId;
  // 'self' 解決を許可するキー（安定した文字列でメモ化する）
  const selfKeysStr = (
    options?.selfKeys ?? ['managerId', 'personInChargeId', 'userId', 'managerUserId']
  ).join(',');

  // searchParams の文字列表現でメモ化（同一クエリでの再計算を防ぐ）
  const search = searchParams.toString();

  const queryParams = useMemo(() => {
    const selfKeys = selfKeysStr ? selfKeysStr.split(',') : [];
    const p: Record<string, string> = {};
    const usp = new URLSearchParams(search);
    usp.forEach((value, key) => {
      if (value === '') return;
      p[key] = value === 'self' && selfUserId && selfKeys.includes(key) ? selfUserId : value;
    });
    return p;
  }, [search, selfUserId, selfKeysStr]);

  const initialFilters = useMemo(
    () => buildInitialFiltersFromQuery(conditions, new URLSearchParams(queryParams)),
    [conditions, queryParams],
  );

  return { queryParams, initialFilters };
}
